import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const nextMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const toLocal = (iso) => { const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const MiniCalendar = ({ mese, items }) => {
  const [y, m] = mese.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const days = new Date(y, m, 0).getDate();
  const lead = (first.getDay() + 6) % 7;
  const byDay = {};
  items.filter((i) => i.data).forEach((i) => { const d = new Date(i.data); if (d.getMonth() === m - 1) (byDay[d.getDate()] ||= []).push(i); });
  return (
    <div className="grid grid-cols-7 gap-1 text-[11px]" data-testid="month-plan-calendar">
      {WD.map((w) => <div key={w} className="text-center font-bold text-slate-500">{w}</div>)}
      {Array.from({ length: lead }).map((_, i) => <div key={`l${i}`} />)}
      {Array.from({ length: days }).map((_, i) => (
        <div key={i} className={`min-h-[44px] rounded-lg border p-1 ${byDay[i + 1] ? "border-[#E8173A] bg-[#E8173A]/10" : "border-slate-200 bg-white"}`}>
          <div className="font-bold text-slate-600">{i + 1}</div>
          {(byDay[i + 1] || []).map((it) => <div key={it.indice} className="truncate rounded bg-[#0A1128] px-1 text-[10px] text-white" title={it.titolo}>{it.titolo}</div>)}
        </div>
      ))}
    </div>
  );
};

export const MonthPlanDialog = ({ open, onOpenChange, analysis }) => {
  const idee = analysis.idee || [];
  const [f, setF] = useState({ mese: nextMonth(), indici: idee.map((_, i) => i), canali: ["instagram", "facebook"], domeniche: false, usa_orari: false });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));
  const preview = async () => {
    if (!f.indici.length || !f.canali.length) return toast.error("Seleziona almeno un'idea e un canale");
    setBusy(true);
    try { setPlan((await api.post(`/admin-ai/competitor/${analysis.id}/mese/anteprima`, f)).data.items); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const create = async () => {
    setBusy(true);
    try {
      const items = plan.filter((i) => !i.gia_nel_ped).map((i) => ({ indice: i.indice, data: new Date(i.data).toISOString() }));
      const r = await api.post(`/admin-ai/competitor/${analysis.id}/mese/crea`, { ...f, items });
      toast.success(`${r.data.creati} bozze create nel PED${r.data.saltati ? ` · ${r.data.saltati} già presenti` : ""}`);
      onOpenChange(false); setPlan(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const todo = useMemo(() => (plan || []).filter((i) => !i.gia_nel_ped).length, [plan]);
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setPlan(null); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="month-plan-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarRange className="h-5 w-5 text-[#E8173A]" />Crea mese nel PED</DialogTitle></DialogHeader>
        {!plan ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-4">
              <label className="text-xs font-bold text-slate-600">Mese<Input type="month" value={f.mese} onChange={(e) => setF({ ...f, mese: e.target.value })} className="mt-1 w-[180px] rounded-xl" data-testid="month-plan-mese" /></label>
              {["instagram", "facebook"].map((c) => (
                <label key={c} className="inline-flex items-center gap-2 text-sm font-semibold capitalize pb-2"><Checkbox checked={f.canali.includes(c)} onCheckedChange={() => toggle("canali", c)} data-testid={`month-plan-canale-${c}`} />{c}</label>
              ))}
              <label className="inline-flex items-center gap-2 text-sm font-semibold pb-2"><Switch checked={f.usa_orari} onCheckedChange={(v) => setF({ ...f, usa_orari: v })} data-testid="month-plan-orari" />Usa orari consigliati</label>
              <label className="inline-flex items-center gap-2 text-sm font-semibold pb-2"><Switch checked={f.domeniche} onCheckedChange={(v) => setF({ ...f, domeniche: v })} data-testid="month-plan-domeniche" />Anche la domenica</label>
            </div>
            <div className="space-y-1.5" data-testid="month-plan-ideas">
              {idee.map((x, i) => (
                <label key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm cursor-pointer">
                  <Checkbox checked={f.indici.includes(i)} onCheckedChange={() => toggle("indici", i)} data-testid={`month-plan-idea-${i}`} />
                  <span className="flex-1 truncate font-semibold">{x.titolo}</span><span className="text-[10px] font-bold uppercase text-slate-500">{x.formato}</span>
                </label>
              ))}
            </div>
            <Button onClick={preview} disabled={busy} className="w-full sm:w-auto rounded-full bg-[#0A1128] font-bold" data-testid="month-plan-preview">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Anteprima calendario</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <MiniCalendar mese={f.mese} items={plan} />
            <div className="space-y-1.5" data-testid="month-plan-items">
              {plan.map((it) => (
                <div key={it.indice} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`month-plan-item-${it.indice}`}>
                  <span className="flex-1 min-w-[160px] truncate font-semibold">{it.titolo}</span>
                  {it.gia_nel_ped ? <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-bold text-slate-700" data-testid={`month-plan-already-${it.indice}`}>già nel PED</span>
                    : <Input type="datetime-local" value={toLocal(it.data)} onChange={(e) => setPlan((p) => p.map((x) => (x.indice === it.indice ? { ...x, data: new Date(e.target.value).toISOString() } : x)))} className="w-[210px] rounded-lg" data-testid={`month-plan-date-${it.indice}`} />}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setPlan(null)} className="rounded-full font-bold" data-testid="month-plan-back">Indietro</Button>
              <Button onClick={create} disabled={busy || !todo} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="month-plan-confirm">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Crea {todo} bozze nel PED</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
